import { format, endOfWeek } from 'date-fns';

/**
 * Parse a date input to a local Date object.
 * Handles both ISO strings and Date objects without any timezone offset manipulation.
 */
const toLocalDate = (dateInput: string | Date): Date => {
  return typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
};

/**
 * Adjusts a date for display. Previously subtracted 4 hours (EDT hardcode);
 * now simply returns the local Date — the browser's local time is correct.
 * Kept for backward compatibility with existing callers.
 */
export const adjustTimeForDisplay = (dateInput: string | Date | null | undefined): Date | null => {
  if (!dateInput) return null;

  try {
    return toLocalDate(dateInput);
  } catch (error) {
    console.error('Error adjusting time:', error);
    return null;
  }
};

/**
 * Formats a date/timestamp for display using the browser's local time.
 * @param dateInput - The date to format (string or Date)
 * @param formatString - The format string (default: 'MMM dd, yyyy h:mm a')
 * @returns Formatted date string in local time
 */
export const formatWithOffset = (
  dateInput: string | Date | null | undefined,
  formatString: string = 'MMM dd, yyyy h:mm a'
): string => {
  if (!dateInput) return 'N/A';

  try {
    const date = toLocalDate(dateInput);
    return format(date, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
};

/**
 * Formats time only (e.g., "2:30 PM") using local time.
 */
export const formatTimeWithOffset = (
  dateInput: string | Date | null | undefined
): string => {
  return formatWithOffset(dateInput, 'h:mm a');
};

/**
 * Formats date only (e.g., "Jan 28, 2025") using local time.
 */
export const formatDateWithOffset = (
  dateInput: string | Date | null | undefined
): string => {
  return formatWithOffset(dateInput, 'MMM dd, yyyy');
};

/**
 * Formats relative time (e.g., "2 hours ago") using local time.
 */
export const formatRelativeWithOffset = (
  dateInput: string | Date | null | undefined
): string => {
  if (!dateInput) return 'N/A';

  try {
    const date = toLocalDate(dateInput);

    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minutes ago`;
    if (hours < 24) return `${hours} hours ago`;

    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;

    return format(date, 'MMM dd');
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'N/A';
  }
};

// ==================== DATE-ONLY UTILITIES (NO TIMEZONE CONVERSION) ====================

/**
 * Formats a date string from YYYY-MM-DD to DD/MM/YYYY
 * IMPORTANT: This function treats dates as calendar dates with NO timezone conversion
 * Use this for PostgreSQL DATE fields (not TIMESTAMP fields)
 *
 * @param dateString - ISO date string in YYYY-MM-DD format
 * @returns Formatted date string in DD/MM/YYYY format, or empty string if invalid
 *
 * @example
 * formatDateOnly('2025-01-15') // Returns '15/01/2025'
 * formatDateOnly('') // Returns ''
 * formatDateOnly(null) // Returns ''
 */
export const formatDateOnly = (dateString: string | null | undefined): string => {
  if (!dateString) return '';

  try {
    // Split YYYY-MM-DD and rearrange to DD/MM/YYYY
    const parts = dateString.split('-');
    if (parts.length !== 3) return dateString; // Return original if not in expected format

    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString; // Return original on error
  }
};

/**
 * Parses a YYYY-MM-DD date string to a Date object WITHOUT timezone conversion
 * IMPORTANT: Only use this when you absolutely need a Date object for comparison
 * For display, use formatDateOnly() instead
 * For sorting, use direct string comparison (YYYY-MM-DD sorts correctly)
 *
 * @param dateString - ISO date string in YYYY-MM-DD format
 * @returns Date object in local timezone, or null if invalid
 *
 * @example
 * parseDateOnly('2025-01-15') // Returns Date object for Jan 15, 2025 at 00:00:00 local time
 */
export const parseDateOnly = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;

  try {
    const [year, month, day] = dateString.split('-').map(Number);
    if (!year || !month || !day) return null;

    // Create date in local timezone (month is 0-indexed)
    return new Date(year, month - 1, day);
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

/**
 * A patient's age in whole years from a DOB (PRX-165 WP-27). Reads the date
 * part through parseDateOnly, so a 'YYYY-MM-DD' or a Kroll timestamp DOB is a
 * local date, never UTC midnight. undefined when the DOB is missing or unreadable.
 */
export const ageFromDob = (dob: string | null | undefined, today: Date = new Date()): number | undefined => {
  const date = parseDateOnly(dob ? dob.split('T')[0] : null);
  if (!date || isNaN(date.getTime())) return undefined;
  let age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) age -= 1;
  return age;
};

/**
 * Get today's date as YYYY-MM-DD string
 * IMPORTANT: Returns a date string with NO timezone conversion
 * Use this instead of format(new Date(), 'yyyy-MM-dd') from date-fns
 *
 * @returns Today's date in YYYY-MM-DD format
 *
 * @example
 * getTodayString() // Returns '2025-10-23'
 */
export const getTodayString = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get TODAY as a YYYY-MM-DD string in the PHARMACY's operating timezone
 * (America/Toronto) — the same calendar day the backend derives in
 * `backend-v2/src/common/utils/date.util.getTodayString()`.
 *
 * `getTodayString()` above reads the BROWSER's clock. That is right for purely
 * local display, but wrong for any date the SERVER will validate: a Winnipeg or
 * BC user working after Toronto midnight is a day behind the server, so a date
 * the UI offered as "today" or "+3 days" comes back rejected as in the past (or
 * silently means one day less than it says). Use this whenever the value is sent
 * to an endpoint that compares it against the pharmacy's today.
 *
 * NOTE: hardcoded to America/Toronto, matching the backend's interim behaviour —
 * per-pharmacy timezone is the eventual fix, and it must change in BOTH places.
 *
 * @returns Today's date in the pharmacy timezone, YYYY-MM-DD
 */
export const getPharmacyTodayString = (): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const part = (type: string) => parts.find((p) => p.type === type)!.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
};

/**
 * True when a date-only value falls strictly AFTER today.
 *
 * Accepts a plain 'YYYY-MM-DD' or a fuller ISO string (the time part is ignored)
 * and compares as strings — YYYY-MM-DD sorts correctly, so no Date parsing and no
 * UTC shift. TODAY is NOT future: an item due today is due now.
 *
 * Mirrors the backend rule in TroubleshootingUnifiedService
 * .releaseAssignmentForFutureFollowUp (GP-1028), which is the authority on what a
 * future follow-up date does to an assignment. Use this for any UI that predicts
 * that outcome, so the two can't drift apart on the boundary.
 *
 * @example
 * isFutureDateOnly('2026-09-01') // true on 2026-08-13
 * isFutureDateOnly('2026-08-13') // false — today is not future
 */
export const isFutureDateOnly = (value: string | null | undefined): boolean => {
  if (!value) return false;
  return String(value).slice(0, 10) > getTodayString();
};

/**
 * The last moment of the CURRENT week — Monday-based, so the boundary is the
 * SUNDAY of the week you are standing in — in local time.
 *
 * THE single definition of "this week" for the queue date presets (GP-1463).
 * The billing queue's "This Week" button, the `'week'` range the shared
 * `queueFilterStore` computes from it, and the clinic-shipments queue that reads
 * that same persisted preset all call this one function, so a user who picks
 * "This Week" on one queue gets the identical window on the other.
 *
 * Saturday and Sunday are inside the window even though the team works Mon-Fri:
 * cutting at Friday would hide a weekend-due item all week and then surface it
 * overdue on Monday.
 *
 * Local arithmetic on purpose — each pharmacy runs in one timezone, so a
 * late-Friday-evening view must not roll into next week (see CLAUDE.md date rules).
 *
 * @example
 * endOfCurrentWeek(new Date(2026, 7, 27)) // Thu Aug 27 2026 → Sun Aug 30 2026 23:59:59.999
 */
export const endOfCurrentWeek = (now: Date = new Date()): Date =>
  endOfWeek(now, { weekStartsOn: 1 });

/**
 * Get the current local date + time as a `YYYY-MM-DDTHH:mm` string, suitable for
 * an <input type="datetime-local"> value.
 * IMPORTANT: Uses the browser's local wall-clock time with NO timezone conversion
 * (each pharmacy operates in a single timezone). Never use `new Date().toISOString()`
 * here — that would shift to UTC and show the wrong time.
 *
 * @returns Current local datetime in `YYYY-MM-DDTHH:mm` format
 *
 * @example
 * getNowDateTimeLocalString() // Returns '2026-07-01T14:30'
 */
export const getNowDateTimeLocalString = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Add or subtract days from a YYYY-MM-DD date string
 * IMPORTANT: Performs date arithmetic without timezone conversion
 * Use this instead of format(addDays(new Date(), n), 'yyyy-MM-dd') from date-fns
 *
 * @param dateString - ISO date string in YYYY-MM-DD format
 * @param days - Number of days to add (positive) or subtract (negative)
 * @returns New date string in YYYY-MM-DD format
 *
 * @example
 * addDays('2025-10-23', 7) // Returns '2025-10-30'
 * addDays('2025-10-23', -7) // Returns '2025-10-16'
 * addDays('2025-10-31', 1) // Returns '2025-11-01' (handles month rollover)
 */
export const addDays = (dateString: string, days: number): string => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);

  const newYear = date.getFullYear();
  const newMonth = String(date.getMonth() + 1).padStart(2, '0');
  const newDay = String(date.getDate()).padStart(2, '0');
  return `${newYear}-${newMonth}-${newDay}`;
};

/**
 * Subtract days from a YYYY-MM-DD date string
 * Convenience wrapper around addDays with negative days
 *
 * @param dateString - ISO date string in YYYY-MM-DD format
 * @param days - Number of days to subtract (positive number)
 * @returns New date string in YYYY-MM-DD format
 *
 * @example
 * subDays('2025-10-23', 7) // Returns '2025-10-16'
 * subDays('2025-11-01', 1) // Returns '2025-10-31' (handles month rollover)
 */
export const subDays = (dateString: string, days: number): string => {
  return addDays(dateString, -days);
};

/**
 * Get day of week from YYYY-MM-DD string
 * @param dateString - ISO date string in YYYY-MM-DD format
 * @returns Day of week (0=Sunday, 1=Monday, ..., 6=Saturday)
 *
 * @example
 * getDayOfWeek('2025-10-23') // Returns the day number for Oct 23, 2025
 */
export const getDayOfWeek = (dateString: string): number => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getDay();
};

/**
 * Get full day name from YYYY-MM-DD string
 * @param dateString - ISO date string in YYYY-MM-DD format
 * @returns Full day name (e.g., 'Monday', 'Tuesday')
 *
 * @example
 * getDayName('2025-10-23') // Returns 'Thursday' (or whatever day it is)
 */
export const getDayName = (dateString: string): string => {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return dayNames[getDayOfWeek(dateString)];
};

/**
 * Get short day name from YYYY-MM-DD string
 * @param dateString - ISO date string in YYYY-MM-DD format
 * @returns Short day name (e.g., 'Mon', 'Tue')
 *
 * @example
 * getShortDayName('2025-10-23') // Returns 'Thu' (or whatever day it is)
 */
export const getShortDayName = (dateString: string): string => {
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return dayNames[getDayOfWeek(dateString)];
};

/**
 * Format a YYYY-MM-DD date string to a long human-readable format
 * IMPORTANT: This function treats dates as calendar dates with NO timezone conversion
 * Use this for PostgreSQL DATE fields (not TIMESTAMP fields)
 *
 * @param dateString - ISO date string in YYYY-MM-DD format
 * @param options - Intl.DateTimeFormatOptions (default: weekday, month, day, year)
 * @returns Formatted date string (e.g., "Monday, March 2, 2026")
 *
 * @example
 * formatDateLong('2026-03-02') // Returns 'Monday, March 2, 2026'
 * formatDateLong('2026-03-02', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
 * // Returns 'Mon, Mar 2, 2026'
 */
export const formatDateLong = (
  dateString: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  },
): string => {
  if (!dateString) return '';

  try {
    const [year, month, day] = dateString.split('-').map(Number);
    if (!year || !month || !day) return dateString;

    // Create date in local timezone (month is 0-indexed) to avoid UTC offset shift
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', options);
  } catch {
    return dateString;
  }
};

/**
 * Format DateTime to simple time string (HH:mm:ss format)
 * @param dateTime - Date object or ISO string
 * @returns Time string in HH:mm:ss format (e.g., "14:30:45")
 */
export const formatTime24 = (dateTime: Date | string | null | undefined): string => {
  if (!dateTime) return '—';
  try {
    const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  } catch {
    return '—';
  }
};

/**
 * Format DateTime to 12-hour time (h:mm a format)
 * @param dateTime - Date object or ISO string
 * @returns Time string in 12-hour format (e.g., "2:30 PM")
 */
export const formatTime12 = (dateTime: Date | string | null | undefined): string => {
  if (!dateTime) return '—';
  try {
    const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  } catch {
    return '—';
  }
};

/**
 * Format DateTime to date and time (DD/MM/YYYY HH:mm format)
 * @param dateTime - Date object or ISO string
 * @returns DateTime string (e.g., "23/10/2025 14:30")
 */
export const formatDateTime = (dateTime: Date | string | null | undefined): string => {
  if (!dateTime) return '—';
  try {
    const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '—';
  }
};